import React from "react";

import PageLayout from "./PageLayout";
// Component imports. Don't forget to import all componets that you want to use in the PageControl component.
import ProductStage from "./Components/Stage/ProductStage";
import TextBox from "./Components/Text/TextBox";
import VideoComponent from "./Components/Media/VideoComponent";
import RichText from "./Components/Text/RichText";
import TwoColumnTextBox from "./Components/Text/TwoColumnTextBox";
import BrokerSearchBox from "./Components/BrokerSearchBox/BrokerSearchBox";
import Footer from "./Components/Footer/Footer";
import ProductSelector from "./Components/ProductSelector/ProductSelector";
import GlidePathAllocation from "./Components/FundData/GlidePathAllocation";
import DocumentDownload from "./Components/DocumentDownload/DocumentDownload";
import CookieInformation from "./Components/CookieOverlay/CookieInformation";
import CookieList from "./Components/CookieOverlay/CookieList";
import CookieButton from "./Components/CookieOverlay/CookieButton";
import PerformanceChart from "./Components/FundData/PerformanceChart";
import PortfolioStructure from "./Components/FundData/PortfolioStructure";
import FundManagerCommentary from "./Components/FundManagerCommentary/FundManagerCommentary";
import DonutChart from "./Components/FundData/DonutChart";
import TopHoldings from "./Components/FundData/TopHoldings";
import GlidePathAllocationWithEntry from "./Components/FundData/GlidePathAllocationWithEntry";


const components = {
  ProductStage: ProductStage,
  TextBox: TextBox,
  VideoComponent: VideoComponent,
  RichText: RichText,
  TwoColumnTextBox: TwoColumnTextBox,
  BrokerSearchBox: BrokerSearchBox,
  Footer: Footer,
  ProductSelector: ProductSelector,
  GlidePathAllocation: GlidePathAllocation,
  DocumentDownload: DocumentDownload,
  CookieInformation: CookieInformation,
  CookieList: CookieList,
  CookieButton: CookieButton,
  PerformanceChart: PerformanceChart,
  PortfolioStructure: PortfolioStructure,
  FundManagerCommentary: FundManagerCommentary,
  DonutChart: DonutChart,
  TopHoldings: TopHoldings,
  GlidePathAllocationWithEntry: GlidePathAllocationWithEntry,
};

/**
 * `PageControl` is a React component that dynamically renders a list of components based on the `content` prop it receives.
 * 
 * The `content` prop is expected to be an array where each element is an object representing a component to be rendered. 
 * The key of the object is the name of the component and the value is the props that should be passed to the component.
 * 
 * The component uses a `components` object where keys are component names and values are the actual component classes or functions.
 * 
 * For each element in the `content` array, `PageControl` determines the component to be rendered by looking up the component name in the `components` object.
 * It then renders the component and spreads the props into the component.
 * 
 * A `key` prop is also assigned to each rendered component, which is required when rendering lists in React.
 * 
 * @example
 * <PageControl pageData={{ content: [{ TextBox: { text: 'Hello' } }, { VideoComponent: { src: 'video.mp4' } }] }} />
 * 
 * @component
 * @param {Object} props - The properties that define the `PageControl` component.
 * @param {Object} props.pageData - The data to be used by the `PageControl` component.
 * @param {Array} props.pageData.content - The array of components to be rendered.
 */
const PageControl = ({ pageData, language }) => {
  if (pageData === null) {
    // Data hasn't been loaded yet, render a loading message or a spinner
    return <div>Loading...</div>;
  } else {

    const pageTranslations = pageData && pageData.translations ? pageData.translations[language] : null;
//    const supportedLanguages = pageData && pageData.translations ? Object.keys(pageData.translations) : [];
    const supportedLanguages = pageData && pageData.translations 
      ? Object.entries(pageData.translations).map(([code, data]) => ({ code, name: data.languageSelectorLabel })) 
      : [];    

    const { content } = pageData;

    return (
      <PageLayout {...pageData} {...pageTranslations} supportedLanguages={supportedLanguages} language={language} navigationEntries={pageTranslations?.navigationEntries} >
        {
          content.map((component, index) => {
            const componentName = Object.keys(component)[0];
            const componentData = component[componentName];
            const translations = componentData && componentData.translations ? componentData.translations[language] : null;
            const Component = components && components[componentName] ? components[componentName] : null;

            if (Component) {
              return (
                <Component
                  key={index}
                  language={language}
                  {...componentData}
                  {...translations}
                />
              );
            }

            // If Component is null, return null so nothing is rendered for this item in the content array
            console.warn(`Component "${componentName}" is missing.`);
            return null;
          })
        }
      </PageLayout>
    );
  };
};

export default PageControl;
